/**
 * Offline utilities for graceful degradation
 */

import { useState, useEffect } from 'react';

export interface NetworkStatus {
  isOnline: boolean;
  isOffline: boolean;
}

export class OfflineManager {
  private static listeners: Array<(status: NetworkStatus) => void> = [];
  private static currentStatus: NetworkStatus = {
    isOnline: navigator.onLine,
    isOffline: !navigator.onLine
  };
  private static initialized = false;

  private static handleOnline() {
    this.currentStatus = { isOnline: true, isOffline: false };
    this.notifyListeners();
    console.log('Network: Back online');
  }

  private static handleOffline() {
    this.currentStatus = { isOnline: false, isOffline: true };
    this.notifyListeners();
    console.log('Network: Gone offline');
  }

  private static initialize() {
    if (this.initialized) return;
    
    // Initialize network status listeners
    window.addEventListener('online', this.handleOnline.bind(this));
    window.addEventListener('offline', this.handleOffline.bind(this));
    this.initialized = true;
  }

  private static notifyListeners() {
    this.listeners.forEach(listener => {
      try {
        listener(this.currentStatus);
      } catch (error) {
        console.error('Error in network status listener:', error);
      }
    });
  }

  /**
   * Get current network status
   */
  static getStatus(): NetworkStatus {
    this.initialize();
    return { ...this.currentStatus };
  }

  /**
   * Check if currently online
   */
  static isOnline(): boolean {
    this.initialize();
    return this.currentStatus.isOnline;
  }

  /**
   * Check if currently offline
   */
  static isOffline(): boolean {
    this.initialize();
    return this.currentStatus.isOffline;
  }

  /**
   * Add a listener for network status changes
   */
  static addListener(listener: (status: NetworkStatus) => void): void {
    this.initialize();
    this.listeners.push(listener);
  }

  /**
   * Remove a network status listener
   */
  static removeListener(listener: (status: NetworkStatus) => void): void {
    const index = this.listeners.indexOf(listener);
    if (index > -1) {
      this.listeners.splice(index, 1);
    }
  }

  /**
   * Attempt a network request with offline fallback
   */
  static async tryNetworkRequest<T>(
    requestFn: () => Promise<T>,
    fallbackFn?: () => T,
    options: { timeout?: number; retries?: number } = {}
  ): Promise<T | null> {
    const { timeout = 5000, retries = 1 } = options;

    if (this.isOffline()) {
      console.log('Network: Skipping request - offline');
      return fallbackFn ? fallbackFn() : null;
    }

    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        // Create a timeout promise
        const timeoutPromise = new Promise<never>((_, reject) => {
          setTimeout(() => reject(new Error('Request timeout')), timeout);
        });

        // Race the request against the timeout
        const result = await Promise.race([requestFn(), timeoutPromise]);
        return result;
      } catch (error) {
        console.warn(`Network request attempt ${attempt + 1} failed:`, error);
        
        if (attempt === retries) {
          // Final attempt failed
          console.log('Network: All attempts failed, using fallback');
          return fallbackFn ? fallbackFn() : null;
        }
        
        // Wait before retry
        await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
      }
    }

    return fallbackFn ? fallbackFn() : null;
  }

  /**
   * Silently attempt a network operation (fail gracefully)
   */
  static async silentNetworkOperation(
    operation: () => Promise<void>,
    operationName: string = 'network operation'
  ): Promise<void> {
    if (this.isOffline()) {
      console.log(`Network: Skipping ${operationName} - offline`);
      return;
    }

    try {
      await operation();
      console.log(`Network: ${operationName} completed successfully`);
    } catch (error) {
      console.log(`Network: ${operationName} failed silently:`, error);
      // Fail silently - don't throw or show errors to user
    }
  }

  /**
   * Queue an operation for when network becomes available
   */
  static queueForOnline(
    operation: () => Promise<void>,
    operationName: string = 'queued operation'
  ): void {
    if (this.isOnline()) {
      // Execute immediately if online
      this.silentNetworkOperation(operation, operationName);
      return;
    }

    // Queue for when online
    const executeWhenOnline = (status: NetworkStatus) => {
      if (status.isOnline) {
        this.removeListener(executeWhenOnline);
        this.silentNetworkOperation(operation, operationName);
      }
    };

    this.addListener(executeWhenOnline);
    console.log(`Network: Queued ${operationName} for when online`);
  }
}

/**
 * React hook for network status
 */
export function useNetworkStatus() {
  const [status, setStatus] = useState<NetworkStatus>(OfflineManager.getStatus());

  useEffect(() => {
    const handleStatusChange = (newStatus: NetworkStatus) => {
      setStatus(newStatus);
    };

    OfflineManager.addListener(handleStatusChange);

    return () => {
      OfflineManager.removeListener(handleStatusChange);
    };
  }, []);

  return status;
}